import { configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { combineReducers } from "redux";

// Import reducers
import authReducer from "./slices/authSlice.js";
import postReducer from "./slices/postSlice.js";
import commentReducer from "./slices/commentsSlice.js";
import { replyReducer } from "./slices/repliesSlice/index.js";
import commentLikesReducer from "./slices/commentLikesSlice.js";
import uiReducer from "./slices/uiSlice.js";
import modalReducer from "./slices/modalSlice.js";
import shareModalReducer from "../features/share/slices/shareModalSlice.js";
import usersReducer from "./slices/usersSlice.js";
import usersCacheReducer from "./slices/usersCacheSlice.js";
import { issueReducer } from "./slices/issueSlice/index.js";
import { profileReducer } from "./slices/profileSlice/index.js";
import savedCommentsReducer from "./slices/savedCommentsSlice.js";
import projectDraftReducer from "./slices/projectDraftSlice.js";
import projectReducer from "./slices/projectSlice.js";
import projectEntitiesReducer from "./slices/projectEntitiesSlice.js";
import projectProgressReducer from "./slices/projectProgressSlice.js";
import {
  solutionReducer as solutionReducerNew,
  solutionViewVotes,
  solutionViewReasons,
  solutionViewReasonLikes,
  solutionViewReasonComments,
  solutionViewReasonCommentLikes,
} from "./solutions/index.js";
import nestedCommentsReducer from "./solutions/solutionView/nestedCommentsSlice.js";
import { waitlistReducer } from "./slices/waitlistSlice/index.js";
import notificationsReducer from "../features/notifications/store/notificationsSlice.js";
import trendingReducer from "./slices/trendingSlice.js";
import feedProjectsReducer from "./slices/feedProjectsSlice.js";
import chatReducer from "./slices/chatSlice.js";
import seenHistoryReducer from "../features/feed/slices/seenHistorySlice.js";

// Root reducer
const rootReducer = combineReducers({
  auth: authReducer,
  posts: postReducer,
  comments: commentReducer,
  replies: replyReducer,
  commentLikes: commentLikesReducer,
  savedComments: savedCommentsReducer,
  ui: uiReducer,
  modal: modalReducer,
  shareModal: shareModalReducer,
  users: usersReducer,
  usersCache: usersCacheReducer,
  issues: issueReducer,
  profile: profileReducer,
  projectDraft: projectDraftReducer,
  projects: projectReducer,
  projectEntities: projectEntitiesReducer,
  projectProgress: projectProgressReducer,
  // Consolidated solution reducer
  solutions: solutionReducerNew,
  solutionViewVotes,
  solutionViewReasons,
  solutionViewReasonLikes,
  solutionViewReasonComments,
  solutionViewReasonCommentLikes,
  solutionViewNestedComments: nestedCommentsReducer,
  waitlist: waitlistReducer,
  notifications: notificationsReducer,
  trending: trendingReducer,
  feedProjects: feedProjectsReducer,
  chat: chatReducer,
  seenHistory: seenHistoryReducer,
});

// Persist configuration - optimized for faster rehydration
// Auth is NOT persisted here - Firebase handles auth persistence automatically
const persistConfig = {
  key: "root",
  storage,
  whitelist: ["projectDraft", "chat"], // Persist project draft and chat state
  serialize: true, // Enable faster serialization
  throttle: 1000, // Throttle persist writes to reduce I/O
};

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Custom serialization middleware to handle Date objects and other non-serializable values
const serializationMiddleware = (store) => (next) => (action) => {
  // Convert action payload Date objects to ISO strings
  if (action.payload) {
    const visited = new WeakSet();

    const convertDates = (obj) => {
      if (obj === null || typeof obj !== "object") return obj;

      // Avoid infinite recursion on circular references
      if (visited.has(obj)) return obj;
      visited.add(obj);

      if (obj instanceof Date) {
        return obj.toISOString();
      }

      // Only deeply traverse plain objects and arrays; leave class instances
      const isArray = Array.isArray(obj);
      const isPlainObject =
        !isArray && Object.getPrototypeOf(obj) === Object.prototype;

      if (!isArray && !isPlainObject) {
        return obj;
      }

      if (isArray) {
        return obj.map(convertDates);
      }

      const result = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          result[key] = convertDates(obj[key]);
        }
      }
      return result;
    };

    action.payload = convertDates(action.payload);
  }

  const result = next(action);
  return result;
};

// Create store with enhanced serialization handling
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Disable the default serializable check since we're handling it manually
        ignoreActions: true,
        ignoreState: true,
      },
    }).concat(serializationMiddleware),
  // Enhanced Redux DevTools configuration
  devTools: {
    name: "WeHeartThem",
    trace: true, // Enable stack traces for dispatched actions
    traceLimit: 25, // Limit the stack trace to 25 calls
  },
});

// Create persistor
export const persistor = persistStore(store);
